import React, { useState } from "react";
import Page from "components/layouts/Page";
import {
  DefaultsAdminGrowthRatesForm,
  BatchDefaultsGrowthRatesFormClient,
  BatchDefaultsGrowthRatesForm,
  ApiException
} from "api";
import RequirePermission from "../RequirePermission";
import BatchDefaultsMenu from "../BatchDefaultsMenu";
import {
  useRemoteData,
  responseHandlers,
  makeRemoteRequest
} from "components/useRemoteData";
import { useApiEndpoint, getApiEndpoint } from "components/useApiEndpoint";
import DefaultsGrowthRatesForm from "components/forms/DefaultsGrowthRatesForm";
import BatchDefaultsBatchInformationArea from "components/forms/BatchDefaultsBatchInformationArea";
import { RouteComponentProps } from "react-router";
import { useContext } from "react";
import { IdentityContext } from "components/IdentityProvider";
import Spinner from "components/controls/Spinner";
import useMessageArea from "components/useMessageArea";
import { BatchDefaultsEditMode } from "components/routes";
import utils from "utils";

interface MatchParams {
  id: string;
  mode: BatchDefaultsEditMode;
}

interface PageProps extends RouteComponentProps<MatchParams> {}

interface PageState {
  form: BatchDefaultsGrowthRatesForm;
  hasFormChanged: boolean;
}

interface LoadingState {
  isLoading: boolean;
}

const BatchDefaultsGrowthRatesPage: React.FC<PageProps> = function (
  props: PageProps
) {
  const batchKey: string = props.match.params.id;
  const batchDefaultsEditMode: BatchDefaultsEditMode = props.match.params.mode;

  const [page, setPage] = useState<PageState>({
    form: {},
    hasFormChanged: false
  });

  const [
    messageAreaState,
    setMessageBasedOnForm,
    setMessageBasedOnApiException
  ] = useMessageArea();

  const setDefaultsForm = function (
    defaultsForm: DefaultsAdminGrowthRatesForm | null,
    hasFormChanged: boolean = true
  ) {
    if (!defaultsForm) {
      return;
    }

    setPage({
      ...page,
      form: {
        ...page.form,
        defaults: {
          ...defaultsForm
        }
      },
      hasFormChanged: hasFormChanged
    });
  };

  const onUpdate = function (form: any): void {
    setDefaultsForm(form.defaults);
  };

  const [dataState] = useRemoteData(
    useApiEndpoint(
      BatchDefaultsGrowthRatesFormClient,
      "getBatchDefaultsGrowthRatesForm"
    ),
    [batchKey],
    {
      autoFetch: true,
      onChange: (d) => {
        setPage({ ...page, form: d || {} });
      }
    }
  );

  const handleSubmitError = function (error: ApiException) {
    responseHandlers.use({
      "400": (ex: ApiException) => {
        const form = ex.result;
        setMessageBasedOnForm(form);
        setPage({ ...page, form: form });
      },
      _: (ex) => {
        setMessageBasedOnApiException(ex);
      }
    })(error);
  };

  const submitEndpoint = "saveBatchDefaultsGrowthRatesForm";
  const [submitState, submit] = useRemoteData(
    useApiEndpoint(BatchDefaultsGrowthRatesFormClient, submitEndpoint),
    [batchKey, page.form],
    {
      onChange: (form) => {
        setMessageBasedOnForm(form);
        onUpdate(form);
      },
      onError: handleSubmitError
    }
  );

  const [loadingState, setLoadingState] = useState<LoadingState>({
    isLoading: false
  });
  const identity = useContext(IdentityContext);
  const saveFormPromise = (): Promise<any> => {
    if (!page.hasFormChanged) {
      return utils.getEmptyPromise();
    }

    setLoadingState({ isLoading: true });

    return makeRemoteRequest(
      getApiEndpoint(
        identity,
        BatchDefaultsGrowthRatesFormClient,
        submitEndpoint
      ),
      [batchKey, page.form]
    )
      .catch((error) => {
        handleSubmitError(error);
        return utils.getRejectedPromise();
      })
      .finally(() => setLoadingState({ isLoading: false }));
  };

  const batch = page.form && page.form.batchDetail;
  const spinner = (
    <Spinner
      isVisible={
        dataState.isFetching || submitState.isFetching || loadingState.isLoading
      }
    />
  );

  //See T-4258 for refactoring improvements
  if (
    (page.form == null || page.form.batchDetail == null) &&
    (dataState.isFetching || submitState.isFetching)
  ) {
    return spinner;
  } else if (page.form == null) {
    return messageAreaState.messageArea;
  }

  if (!page.form.defaults) {
    return <span>Error with current defaults.</span>;
  }

  if (!batch) {
    return <span>Error with batch information.</span>;
  }

  const menu = (
    <BatchDefaultsMenu
      onClick={saveFormPromise}
      batchKey={batchKey}
      title={"GROWTH RATES"}
      batchDefaultsEditMode={batchDefaultsEditMode}
      hasUnsavedChanges={page.hasFormChanged}
      pageConfiguration={page.form.defaults.pageConfiguration}
    />
  );

  return (
    <Page menu={menu}>
      <RequirePermission
        permissions={["canViewBatchDefaultsForms"]}
        accessDenied="page"
      >
        {spinner}
        {messageAreaState.messageArea}
        <BatchDefaultsBatchInformationArea batchInformation={{ ...batch }} />
        <DefaultsGrowthRatesForm
          isFetching={submitState.isFetching}
          isSuccessful={submitState.isSuccessful}
          submit={() => submit([])}
          setForm={setDefaultsForm}
          form={page.form.defaults}
          hideDivisions={true}
          hideSaveButton={true}
          pageConfiguration={page.form.defaults.pageConfiguration}
        />
      </RequirePermission>
    </Page>
  );
};

export default BatchDefaultsGrowthRatesPage;
